import type { AxiosResponse } from 'axios';
import { AttachmentImageBA } from '../../sdk/type';
import { LoggerService } from './logger';

const VALID_FILE_TYPES = ['image/jpeg', 'image/jpg', 'image/svg+xml', 'image/png', 'image/tiff', 'image/gif'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

export const img2base64 = (file: File) =>
  // This is use to display image before upload
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
  });

export const validateImageAttachment = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (e.target && e.target.files && e.target.files.length > 0) {
    const file = e.target.files;
    const isFileSizeValid = file[0].size <= MAX_FILE_SIZE; // Valid File Size
    const isFileTypeValid = VALID_FILE_TYPES.includes(file[0].type); // Valid File Type

    return isFileSizeValid && isFileTypeValid;
  }
};

export const processBeforeAfterImagesData = (
  images: Record<string, AttachmentImageBA>,
  campaignId: string,
  teamId = '',
  loginId = '',
) => {
  const formData = new FormData();
  let isRemovedAppended = false;

  for (const [key, value] of Object.entries(images)) {
    if (value.photoId !== 0) {
      if (!value.file && !value.url && !isRemovedAppended) {
        formData.append('isRemoved', '1');
        isRemovedAppended = true;
      }
    }
    formData.append(`photoId.${value.photoId},${key}`, value.file ?? value.url ?? '');
  }

  formData.append('campaignId', campaignId);
  formData.append('loginId', loginId);
  formData.append('teamId', teamId);

  return formData;
};

export const processJoinTeamImageData = (images: Record<string, AttachmentImageBA>, payload: string) => {
  const formData = new FormData();
  let isRemovedAppended = false;

  for (const [key, value] of Object.entries(images)) {
    if (value.photoId !== 0) {
      if (!value.file && !value.url && !isRemovedAppended) {
        formData.append('isRemoved', '1');
        isRemovedAppended = true;
      }
    }
    formData.append(`photoId.${value.photoId},${key}`, value.file ?? value.url ?? '');
  }

  formData.append('payload', payload);

  formData.forEach((value, key) => {
    LoggerService.log(`${key}:`, value);
  });

  return formData;
};

export const processBeforeAfterImagesResponse = (
  res: AxiosResponse,
  existingImgs: Record<string, AttachmentImageBA>,
): Record<string, AttachmentImageBA> => {
  if (res.data.result && res.data.data && res.data.data.image) {
    const imageData = res.data.data.image;
    const updatedImages = { ...existingImgs };

    imageData.forEach((image: { photoId: number; photoType: string; url: string }) => {
      const { photoId, photoType, url } = image;
      if (Object.prototype.hasOwnProperty.call(updatedImages, photoType)) {
        updatedImages[photoType].photoId = photoId;
        updatedImages[photoType].url = url;
      }
    });
    return updatedImages;
  }
  return existingImgs;
};

export const processProfilePictureData = (image: File, loginId: string) => {
  const formData = new FormData();
  formData.append('file', image);
  formData.append('loginId', loginId);
  formData.append('isProfile', '1');
  return formData;
};

export const base64ToBlob = (base64: string, contentType: string = 'image/png'): Blob => {
  // Remove the data URI scheme prefix if it exists
  const base64Clean = base64.replace(/^data:image\/(png|jpg|jpeg);base64,/, '');
  const byteCharacters = atob(base64Clean);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += 512) {
    const slice = byteCharacters.slice(offset, offset + 512);
    const byteNumbers = new Array(slice.length);

    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  return new Blob(byteArrays, { type: contentType });
};

export const blobToFile = (blob: Blob, fileName: string): File => {
  return new File([blob], fileName, { type: blob.type });
};
